import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import { showErrorMessage } from '@acorn-portal/shared/src/components/toast/messageUtils';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useGetMileageOptions from '@acorn-portal/shared/src/hooks/useGetMileageOptions';
import useNoChangeWarning from '@acorn-portal/shared/src/hooks/useNoChangeWarning';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	PcQuoteDetails,
	PcRestartResponse,
	PrivateCarClient,
	UpdatePrivateCarQuoteCommand,
} from '@acorn-portal/shared/src/services/subscriptionAPI';
import { updateSubscriptionData } from '@acorn-portal/shared/src/store/subscription.slice';
import {
	resetSubscriptionChanges,
	updateChangeOfAddress,
} from '@acorn-portal/shared/src/store/subscriptionChanges.slice';
import { isUsedForBusiness } from '@acorn-portal/shared/src/utils';
import {
	ANNUAL_MILEAGE,
	BUSINESS_MILEAGE,
	OVERNIGHT_ADDRESS_DIFFERENT,
} from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Box, Collapse } from '@mui/material';
import { Form, Formik } from 'formik';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import changeOfAddressSchema from '../validation/change-of-address-schema';
import { getAddressInitialValues, IFormChangeAddress } from './utils/get-address-initial-values';
import { getFormattedFormValues } from './utils/get-formatted-form-values';
import { getOvernightAddress } from './utils/get-overnight-address';
import { getProposerDetails } from './utils/get-proposer-details';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

const ChangeOfAddress = () => {
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const lists = useAppSelector((redux) => redux.referenceData);
	const subscriptionData = useAppSelector((state) => state.subscription);
	const storeProposer = useAppSelector((state) => state.subscription.quoteData?.quoteDetails.riskData.proposerDetails);
	const storeVehicle = useAppSelector((state) => state.subscription.quoteData?.quoteDetails.riskData.vehicleDetails);
	const dispatch = useAppDispatch();
	const { annualMileageOptions, getBusinessMileageOptions } = useGetMileageOptions();
	const [hasChanged] = useNoChangeWarning<IFormChangeAddress>();
	const quoteData = useMemo(() => subscriptionData.quoteData, [subscriptionData.quoteData]);
	const initialValues = useMemo(
		() => getAddressInitialValues(quoteData, annualMileageOptions),
		[quoteData, annualMileageOptions],
	);
	const businessUse = isUsedForBusiness(
		subscriptionData.quoteData?.quoteDetails.riskData.vehicleDetails?.vehicleUse ?? '',
	);
	const baseVehicleDetails = storeVehicle || quoteData?.quoteDetails.riskData.vehicleDetails;

	const handleOnSubmit = async (values: IFormChangeAddress) => {
		if (!baseVehicleDetails) throw new Error('Vehicle details are not available');
		if (!storeProposer) throw new Error('Proposer details are not available');
		if (!storeVehicle) throw new Error('Vehicle details are not available');

		const formattedValues = getFormattedFormValues(values, annualMileageOptions);

		if (!hasChanged(initialValues, formattedValues)) {
			showErrorMessage('You are unable to proceed as you did not make any changes to you policy');
			return;
		}
		const vehicleDetails = { ...baseVehicleDetails, overnightAddress: getOvernightAddress(formattedValues) };
		const proposerDetails = getProposerDetails(formattedValues, storeProposer);

		setLoading(true);
		try {
			const url = import.meta.env.VITE_SUBSCRIPTION_API;
			const subscriptionClient = new PrivateCarClient(url, API);

			const body: UpdatePrivateCarQuoteCommand = {
				quoteReference: quoteData?.quoteResponse.quoteRef ?? '',
				quoteToken: quoteData?.quoteToken ?? '',
				quoteDetails: {
					...quoteData?.quoteDetails,
					riskData: {
						...quoteData?.quoteDetails.riskData,
						vehicleDetails,
						proposerDetails,
					},
				} as PcQuoteDetails,
			};
			const result: PcRestartResponse = await subscriptionClient.updateQuote(body);

			if (result.quoteResponse.underwrittenOptions?.[0]?.status === 'Declined') {
				navigate(ROUTE_PATHS.subscriptionRestartReferral);
				return;
			}

			if (!result) {
				dispatch(resetSubscriptionChanges());
				throw new Error('Rating response is undefined');
			}

			dispatch(
				updateSubscriptionData({
					policyToken: subscriptionData.policyToken,
					effectiveDate: subscriptionData.effectiveDate,
					quoteData: result,
					sendWelcomePackStatus: undefined,
					autoRenew: undefined,
				}),
			);

			dispatch(updateChangeOfAddress(true));
			navigate(`${ROUTE_PATHS.subscriptionRestartSummary}/${subscriptionData.policyToken}`);
		} catch (error) {
			console.error('Error during change of address rating:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	return (
		<JourneyCardLayout pageTitle='Change Of Address' loading={loading}>
			<Formik<IFormChangeAddress>
				initialValues={initialValues}
				validationSchema={changeOfAddressSchema({ businessUse: businessUse })}
				onSubmit={handleOnSubmit}
			>
				{(formik) => {
					return (
						<Form>
							<FormikLogErrors />
							<AddressLookup
								initialValue={formik.values.address}
								onChange={(value) => {
									formik.setFormikState((formState) => ({
										...formState,
										values: {
											...formState.values,
											address: {
												...formState.values.address,
												line1: value?.line1 ?? '',
												line2: value?.line2,
												line3: value?.line3,
												line4: value?.line4,
												city: value?.city ?? '',
												county: value?.county,
												country: value?.country ?? '',
												postcode: value?.postcode ?? '',
											},
										},
									}));
								}}
								globalError={formik.touched.address?.line1 && Boolean(formik.errors.address?.line1)}
							/>
							<SelectFormField
								label='Annual mileage'
								name='annualMileage'
								id='annualMileage'
								value={formik.values.annualMileage}
								onChange={(value) => {
									formik.setFieldValue('annualMileage', value.toString());
								}}
								options={annualMileageOptions}
								modal={{
									title: ANNUAL_MILEAGE.header,
									description: ANNUAL_MILEAGE.text,
								}}
								error={formik.touched.annualMileage && Boolean(formik.errors.annualMileage)}
								helperText={formik.touched.annualMileage && formik.errors.annualMileage}
							/>
							<Collapse in={isUsedForBusiness(storeVehicle?.vehicleUse ?? '')}>
								<Box>
									<SelectFormField
										label='Business mileage'
										name='businessMileage'
										id='businessMileage'
										value={formik.values.businessMileage}
										onChange={(value) => {
											formik.setFieldValue('businessMileage', value.toString());
										}}
										options={getBusinessMileageOptions(formik.values.annualMileage)}
										modal={{
											title: BUSINESS_MILEAGE.header,
											description: BUSINESS_MILEAGE.text,
										}}
										error={formik.touched.businessMileage && Boolean(formik.errors.businessMileage)}
										helperText={formik.touched.businessMileage && formik.errors.businessMileage}
									/>
								</Box>
							</Collapse>
							<ToggleButtonFormField
								label='Where is the vehicle kept overnight?'
								name='keptOvernight'
								id='keptOvernight'
								value={formik.values.keptOvernight}
								onChange={(value) => {
									formik.setFieldValue('keptOvernight', value);
								}}
								options={lists.vehicleStorageOptions as IOptions[]}
								error={formik.touched.keptOvernight && Boolean(formik.errors.keptOvernight)}
								helperText={formik.touched.keptOvernight && formik.errors.keptOvernight}
							/>
							<RadioGroupButton
								label='Is your home address different to the where your vehicle is kept overnight?'
								name='overnightAddressIsAway'
								id='overnightAddressIsAway'
								value={formik.values.overnightAddressIsAway}
								onChange={(value) => {
									formik.setFieldValue('overnightAddressIsAway', value);

									if (value === 'no') {
										formik.setFieldValue('overnightAddress', { ...formik.values.address });
									}
								}}
								error={formik.touched.overnightAddressIsAway && Boolean(formik.errors.overnightAddressIsAway)}
								helperText={formik.touched.overnightAddressIsAway && formik.errors.overnightAddressIsAway}
								modal={{
									title: OVERNIGHT_ADDRESS_DIFFERENT.title,
									description: OVERNIGHT_ADDRESS_DIFFERENT.description,
								}}
							/>
							<Collapse in={formik.values.overnightAddressIsAway === 'yes'}>
								<AddressLookup
									initialValue={formik.values.overnightAddress}
									firstFieldLabel='Overnight House number or Name'
									postcodeLabel='Overnight Postcode'
									onChange={(value) => {
										formik.setFieldValue('overnightAddress', {
											...formik.values.overnightAddress,
											line1: value?.line1 ?? '',
											line2: value?.line2,
											line3: value?.line3,
											line4: value?.line4,
											city: value?.city ?? '',
											county: value?.county,
											country: value?.country ?? '',
											postcode: value?.postcode ?? '',
										});
									}}
								/>
							</Collapse>
							<RadioGroupButton
								label='Are you a homeowner?'
								name='isHomeOwner'
								id='isHomeOwner'
								value={formik.values.isHomeOwner}
								onChange={(value) => {
									formik.setFieldValue('isHomeOwner', value);
								}}
								error={formik.touched.isHomeOwner && Boolean(formik.errors.isHomeOwner)}
								helperText={formik.touched.isHomeOwner && formik.errors.isHomeOwner}
							/>
							<ActionButtons>
								<BackButton onClick={() => setOpen(true)}>Back</BackButton>
								<ContinueButton type='submit'>Continue</ContinueButton>
							</ActionButtons>
							<HelperModal
								open={open}
								onClose={() => {
									setOpen(false);
								}}
								title='Discard The Changes?'
								description='Are you sure you wish to discard the changes?'
								closeText='No'
								submitText='Yes'
								onSubmit={() => {
									formik.resetForm();
									navigate(-1);
								}}
							/>
						</Form>
					);
				}}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeOfAddress;
